import React, { useCallback, useEffect, useState } from 'react';
import { Icon, STATUS_MAP, StatusChip } from '../components/sp-primitives';
import { DeletePaymentConfirmDialog, Modal } from '../components/sp-overlays';
import { PShell, SpWorkspaceLoading, useApp } from '../components/sp-shell';
import { CONFIG_BACKEND } from '../config-backend';
import { ROUTES } from '../routes';

function normalizeDoc(v) {
  return String(v || '').replace(/\D+/g, '');
}

function formatDocument(v) {
  const d = normalizeDoc(v);
  if (d.length === 11) {
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  if (d.length === 14) {
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  }
  return v;
}

function normalizeForDocumentNumber(value) {
  if (value === undefined || value === null) return '[ausente]';
  const s = String(value).trim();
  return s.length ? s : '[ausente]';
}

function resolveDocumentNumberKind(template) {
  return String(template?.paymentType || '').toLowerCase() === 'individual' ? 'IND' : 'LOTE';
}

function buildDocumentNumber({ template, documentIdentificationId, creditorId, dueDate }) {
  const docKind = resolveDocumentNumberKind(template);
  const docId = normalizeForDocumentNumber(documentIdentificationId);
  const credId = normalizeForDocumentNumber(creditorId);
  const due = normalizeForDocumentNumber(dueDate);
  return `API-RH-${docKind}-${docId}-${credId}-${due}`;
}

function mapCompanyPaymentToSiengeRow(p) {
  const creditorName = (p.company_creditor && p.company_creditor.name) || p.creditor_name || '—';
  const syncStatus = String(p.sienge_sync_status || '').toLowerCase();
  const billId = p.sienge_billId != null && String(p.sienge_billId).trim() !== ''
    ? String(p.sienge_billId).trim()
    : '—';
  const doc = p.sienge_document_id != null && String(p.sienge_document_id).trim() !== ''
    ? String(p.sienge_document_id).trim()
    : '—';
  const amount = Number(p.sienge_totalInvoiceAmount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  if (syncStatus === 'synced') {
    return { id: p.id, creditorName, doc, docKind: 'mono', statusLabel: 'Transmitido', statusKind: 'green', billId, amount };
  }
  if (syncStatus === 'pending' || syncStatus === 'processing') {
    return { id: p.id, creditorName, doc, docKind: 'mono', statusTag: 'Em transmissão', statusKind: 'amber', billId, amount };
  }
  if (syncStatus === 'cancelled') {
    return { id: p.id, creditorName, docTag: 'Rejeitado', docKind: 'chip-purple', statusTag: 'Rejeitado', statusKind: 'purple', billId, amount };
  }
  if (syncStatus === 'failed') {
    return { id: p.id, creditorName, docTag: 'Falha', docKind: 'chip-red', statusTag: 'Falha', statusKind: 'red', billId, amount };
  }
  // syncStatus === null (não foi aprovado ainda)
  const rawStatus = String(p.status || '').toLowerCase();
  if (rawStatus === 'pending') {
    return { id: p.id, creditorName, docTag: 'Aguardando pendência', docKind: 'chip-amber', statusTag: 'Aguardando pendência', statusKind: 'amber', billId, amount };
  }
  return { id: p.id, creditorName, docTag: 'Aguardando aprovação', docKind: 'chip-amber', statusTag: 'Aguardando aprovação', statusKind: 'amber', billId, amount };
}

export function PBatchDetails() {
  const app = useApp();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [approvalBlockedModal, setApprovalBlockedModal] = useState(false);
  const [paymentInSyncQueue, setPaymentInSyncQueue] = useState(false);
  const [siengeRows, setSiengeRows] = useState([]);
  const [siengeLoading, setSiengeLoading] = useState(false);
  const [verifyingPending, setVerifyingPending] = useState(false);
  const [identificationChecks, setIdentificationChecks] = useState([]);
  const [detailsReady, setDetailsReady] = useState(false);
  const [updatingPaymentData, setUpdatingPaymentData] = useState(false);
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [billModalLoading, setBillModalLoading] = useState(false);
  const [billModalData, setBillModalData] = useState(null);
  const [billModalError, setBillModalError] = useState(null);
  const [paymentCategoryOptions, setPaymentCategoryOptions] = useState([]);
  const [documentIdentificationOptions, setDocumentIdentificationOptions] = useState([]);
  const [siengeCompanyOptions, setSiengeCompanyOptions] = useState([]);
  const [siengeCostCenterOptions, setSiengeCostCenterOptions] = useState([]);
  const [creditorOptions, setCreditorOptions] = useState([]);
  const [creditorSearch, setCreditorSearch] = useState('');
  const [selectedCreditorId, setSelectedCreditorId] = useState('');
  const [paymentCreditorSiengeId, setPaymentCreditorSiengeId] = useState('');
  const [approvalPopupShown, setApprovalPopupShown] = useState(false);
  const [rejectionInfo, setRejectionInfo] = useState(null);
  const [paymentDataForm, setPaymentDataForm] = useState({
    creditorName: '',
    document: '',
    sienge_totalInvoiceAmount: '',
    sienge_paymentCategoriesId: '',
    sienge_documentIdentificationId: '',
    sienge_debtorId: '',
    sienge_costCenterId: '',
    sienge_baseDate: '',
    sienge_dueDate: '',
    documentNumber: '',
    sienge_notes: '',
  });
  const [paymentDataErrors, setPaymentDataErrors] = useState({
    creditorName: false,
    document: false,
    sienge_totalInvoiceAmount: false,
    sienge_paymentCategoriesId: false,
    sienge_documentIdentificationId: false,
    sienge_debtorId: false,
    sienge_costCenterId: false,
    sienge_baseDate: false,
    sienge_dueDate: false,
    sienge_notes: false,
  });
  const batch = app.batches.find((b) => b.id === app.params.id) || {
    id: '-',
    lotId: null,
    status: 'draft',
    ref: '-',
    template: '-',
    file: '-',
    date: '-',
    createdAtLabel: '—',
    createdByName: '—',
    identifiedLocal: false,
    identifiedSienge: false,
    total: 'R$ 0,00',
    colab: 0,
    diffs: 0,
  };
  const createdLabel = batch.createdAtLabel ?? batch.date ?? '—';
  const creatorName = batch.createdByName ?? '—';
  const routeImportId = Number(String(app.params.id || '').replace(/\D+/g, '')) || 0;
  const effectiveImportId = Number(batch.lotId || routeImportId || 0);
  const localOk = batch.identifiedLocal === true;
  const siengeOk = batch.identifiedSienge === true;
  const s = STATUS_MAP[batch.status];
  const templateLabel = String(batch.template || '').split(' - ')[0];
  const selectedTemplate = (app.templates || []).find((t) => String(t.n || '') === String(templateLabel) || String(t.n || '') === String(batch.template || ''));
  const documentFieldLabel = selectedTemplate?.strat === 'BY_CNPJ' ? 'CNPJ' : 'CPF';
  const requiredDocLength = selectedTemplate?.strat === 'BY_CNPJ' ? 14 : 11;
  const permissions = Array.isArray(app.currentUser?.permissions) ? app.currentUser.permissions : [];
  const roleText = String(app.currentUser?.role || '').toLowerCase();
  const isAdmin = permissions.includes('Administrador');
  const isRegistrar = permissions.includes('Registrador') || roleText.includes('registrador');
  const isOwner = Number(batch.createdById || 0) > 0 && Number(batch.createdById) === Number(app.currentUser?.id || 0);
  const canMutatePayment = !paymentInSyncQueue && (isAdmin || !isRegistrar || (isOwner && batch.status !== 'approval'));
  const approvalBlocked = !paymentInSyncQueue && isRegistrar && !isAdmin && isOwner && batch.status === 'approval';
  const syncBlocked = paymentInSyncQueue;
  const canDelete = batch.id !== '-' && batch.status !== 'transmitted' && !paymentInSyncQueue && (isAdmin || !isRegistrar || isOwner);
  const orderedIdentificationChecks = [...identificationChecks].reverse();

  const openDelete = useCallback(() => {
    setDeleteOpen(true);
  }, []);

  const closeDelete = useCallback(() => {
    if (deleteBusy) return;
    setDeleteOpen(false);
  }, [deleteBusy]);

  const confirmDelete = useCallback(async () => {
    if (batch.id === '-') return;
    if (!canMutatePayment) {
      app.toast('error', 'Sem permissão', 'Registradores só podem excluir pagamentos criados por eles.');
      return;
    }
    setDeleteBusy(true);
    try {
      const ok = await app.deleteBatch(batch.id);
      if (ok) {
        setDeleteOpen(false);
        app.navigate(ROUTES.PAYMENTS);
      }
    } finally {
      setDeleteBusy(false);
    }
  }, [app, batch.id, canMutatePayment]);

  const loadImportDetails = useCallback(async (lotId, cancelledRef) => {
    try {
      const data = await app.fetchLotDetail(lotId);
      if (cancelledRef?.current) return;
      const list = Array.isArray(data?.company_payments) ? data.company_payments : [];
      setSiengeRows(list.map(mapCompanyPaymentToSiengeRow));
      const firstPayment = list[0];
      const syncing = ['pending', 'processing', 'synced'].includes(String(firstPayment?.sienge_sync_status || ''));
      setPaymentInSyncQueue(syncing);
      const checks = Array.isArray(firstPayment?.identification_checks) ? firstPayment.identification_checks : [];
      setIdentificationChecks(checks);
      setPaymentCreditorSiengeId(String(firstPayment?.sienge_creditorId ?? ''));
      setPaymentDataForm({
        creditorName: String(firstPayment?.creditor_name || firstPayment?.company_creditor?.name || ''),
        document: String(firstPayment?.document || ''),
        sienge_totalInvoiceAmount: String(firstPayment?.sienge_totalInvoiceAmount ?? ''),
        sienge_paymentCategoriesId: String(firstPayment?.sienge_paymentCategoriesId || ''),
        sienge_documentIdentificationId: String(
          firstPayment?.sienge_documentIdentificationId
          || firstPayment?.sienge_bill_payload?.documentIdentificationId
          || '',
        ),
        sienge_debtorId: String(
          firstPayment?.sienge_debtorId
          || firstPayment?.sienge_bill_payload?.debtorId
          || '',
        ),
        sienge_costCenterId: String(
          firstPayment?.sienge_costCenterId
          || firstPayment?.sienge_bill_payload?.BillBudgetCategoriesInsert?.[0]?.costCenterId
          || '',
        ),
        sienge_baseDate: firstPayment?.sienge_baseDate
          ? String(firstPayment.sienge_baseDate).slice(0, 10)
          : '',
        sienge_dueDate: firstPayment?.sienge_dueDate ? String(firstPayment.sienge_dueDate).slice(0, 10) : '',
        documentNumber: String(
          firstPayment?.sienge_documentNumber
          || buildDocumentNumber({
            template: selectedTemplate,
            documentIdentificationId: firstPayment?.sienge_documentIdentificationId,
            creditorId: firstPayment?.sienge_creditorId,
            sienge_dueDate: firstPayment?.sienge_dueDate ? String(firstPayment.sienge_dueDate).slice(0, 10) : '',
          }),
        ),
          sienge_notes: String(firstPayment?.sienge_notes || ''),
      });
      const rejectedApproval = Array.isArray(data?.approvals)
        ? data.approvals.find((a) => a.status === 'rejected')
        : null;
      setRejectionInfo(rejectedApproval ? {
        reviewer: rejectedApproval.reviewer?.name || '—',
        reviewedAt: rejectedApproval.reviewed_at || null,
        sienge_notes: rejectedApproval.notes || null,
      } : null);
      setPaymentDataErrors({
        creditorName: false,
        document: false,
        sienge_totalInvoiceAmount: false,
        sienge_paymentCategoriesId: false,
        sienge_documentIdentificationId: false,
        sienge_debtorId: false,
        sienge_costCenterId: false,
        sienge_baseDate: false,
        sienge_dueDate: false,
        sienge_notes: false,
      });
    } catch {
      if (!cancelledRef?.current) {
        setSiengeRows([]);
        setPaymentInSyncQueue(false);
        setIdentificationChecks([]);
        setPaymentCreditorSiengeId('');
        setPaymentDataForm({
          creditorName: '',
          document: '',
          sienge_totalInvoiceAmount: '',
          sienge_paymentCategoriesId: '',
          sienge_documentIdentificationId: '',
          sienge_debtorId: '',
          sienge_costCenterId: '',
          sienge_baseDate: '',
          sienge_dueDate: '',
          documentNumber: '',
          sienge_notes: '',
        });
        setPaymentDataErrors({
          creditorName: false,
          document: false,
          sienge_totalInvoiceAmount: false,
          sienge_paymentCategoriesId: false,
          sienge_documentIdentificationId: false,
          sienge_debtorId: false,
          sienge_costCenterId: false,
          sienge_baseDate: false,
          sienge_dueDate: false,
          sienge_notes: false,
        });
      }
    }
  }, [app, selectedTemplate]);

  useEffect(() => {
    const next = buildDocumentNumber({
      template: selectedTemplate,
      documentIdentificationId: paymentDataForm.sienge_documentIdentificationId,
      creditorId: paymentCreditorSiengeId,
      sienge_dueDate: paymentDataForm.sienge_dueDate,
    });
    if (paymentDataForm.documentNumber === next) return;
    setPaymentDataForm((prev) => ({ ...prev, documentNumber: next }));
  }, [
    selectedTemplate,
    paymentDataForm.sienge_documentIdentificationId,
    paymentDataForm.sienge_dueDate,
    paymentDataForm.documentNumber,
    paymentCreditorSiengeId,
  ]);

  useEffect(() => {
    let cancelled = false;
    const cancelledRef = { current: false };
    if (!effectiveImportId || batch.id === '-') {
      if (!app.workspaceReady || app.isSyncing) {
        setDetailsReady(false);
        return undefined;
      }
      setSiengeLoading(false);
      setDetailsReady(true);
      return undefined;
    }
    setDetailsReady(false);
    setSiengeLoading(true);
    (async () => {
      try {
        await loadImportDetails(effectiveImportId, cancelledRef);
      } finally {
        if (!cancelled) {
          setSiengeLoading(false);
          setDetailsReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
      cancelledRef.current = true;
    };
  }, [effectiveImportId, batch.id, loadImportDetails, app.workspaceReady, app.isSyncing]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await app.fetchSiengeFieldsOptions();
        if (cancelled) return;
        const effective = data?.effective || {};
        setPaymentCategoryOptions(Array.isArray(effective.payment_categories) ? effective.payment_categories : []);
        setDocumentIdentificationOptions(Array.isArray(effective.document_identifications) ? effective.document_identifications : []);
      } catch {
        if (!cancelled) {
          setPaymentCategoryOptions([]);
          setDocumentIdentificationOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [app]);

  useEffect(() => {
    if (!app.activeCompanyId) return undefined;
    let mounted = true;
    const ac = new AbortController();
    (async () => {
      try {
        const first = await app.fetchCompanyCreditors(app.activeCompanyId, { page: '1', per_page: '200' }, { signal: ac.signal });
        const all = [...(first?.data || [])];
        const pages = Number(first?.last_page || 1);
        for (let p = 2; p <= pages && p <= 5; p += 1) {
          const next = await app.fetchCompanyCreditors(app.activeCompanyId, { page: String(p), per_page: '200' }, { signal: ac.signal });
          all.push(...(next?.data || []));
        }
        if (mounted) setCreditorOptions(all);
      } catch {
        if (mounted) setCreditorOptions([]);
      }
    })();
    return () => {
      mounted = false;
      ac.abort();
    };
  }, [app, app.activeCompanyId]);

  useEffect(() => {
    if (!app.activeCompanyId) return undefined;
    let mounted = true;
    const ac = new AbortController();
    (async () => {
      try {
        const data = await app.fetchCompanyFieldsCompanies(app.activeCompanyId, { signal: ac.signal });
        if (!mounted) return;
        setSiengeCompanyOptions(Array.isArray(data?.data) ? data.data : []);
      } catch {
        if (mounted) setSiengeCompanyOptions([]);
      }
    })();
    return () => {
      mounted = false;
      ac.abort();
    };
  }, [app, app.activeCompanyId]);

  useEffect(() => {
    if (!app.activeCompanyId) return undefined;
    const companyId = String(paymentDataForm.sienge_debtorId || '').trim();
    if (!companyId) {
      setSiengeCostCenterOptions([]);
      return undefined;
    }
    let mounted = true;
    const ac = new AbortController();
    (async () => {
      try {
        const data = await app.fetchCompanyFieldsCostCenters(
          app.activeCompanyId,
          { sienge_debtorId: companyId },
          { signal: ac.signal },
        );
        if (!mounted) return;
        setSiengeCostCenterOptions(Array.isArray(data?.data) ? data.data : []);
      } catch {
        if (mounted) setSiengeCostCenterOptions([]);
      }
    })();
    return () => {
      mounted = false;
      ac.abort();
    };
  }, [app, app.activeCompanyId, paymentDataForm.sienge_debtorId]);

  useEffect(() => {
    if (!detailsReady || approvalPopupShown) return;
    if (batch.status === 'rejected') {
      setApprovalPopupShown(true);
      window.alert('Este pagamento foi rejeitado, você tem 3 dias para reenviá-lo para aprovação ou ele será arquivado automaticamente.');
      return;
    }
    const hasAwaitingApproval = batch.status === 'approval'
      || siengeRows.some((row) => row.statusTag === 'Aguardando aprovação');
    if (!hasAwaitingApproval) return;
    setApprovalPopupShown(true);
    window.alert('Este pagamento já foi encaminhado para aprovação');
  }, [detailsReady, approvalPopupShown, batch.status, siengeRows]);

  const verifyPendencies = useCallback(async () => {
    if (!effectiveImportId) return;
    if (!canMutatePayment) {
      app.toast('error', 'Sem permissão', 'Registradores só podem alterar pagamentos criados por eles.');
      return;
    }
    try {
      setVerifyingPending(true);
      const result = await app.verifyLotPendencies(effectiveImportId);
      app.toast('info', 'Verificação de pendência', result?.message || 'Verificação concluída.');
      await app.syncFromApi(undefined, 'batches');
      await loadImportDetails(effectiveImportId, { current: false });
    } catch (error) {
      app.toast('error', 'Verificação de pendência', error?.message || 'Falha ao verificar pendências.');
    } finally {
      setVerifyingPending(false);
    }
  }, [app, effectiveImportId, loadImportDetails, canMutatePayment]);

  const updatePaymentData = useCallback(async () => {
    if (!effectiveImportId) return;
    if (!canMutatePayment) {
      app.toast('error', 'Sem permissão', 'Registradores só podem editar pagamentos criados por eles.');
      return;
    }
    const normalizedDoc = normalizeDoc(paymentDataForm.document);
    const nextErrors = {
      creditorName: !String(paymentDataForm.creditorName || '').trim(),
      document: !normalizedDoc || normalizedDoc.length !== requiredDocLength,
      sienge_totalInvoiceAmount: !(Number(paymentDataForm.sienge_totalInvoiceAmount) > 0),
      sienge_paymentCategoriesId: !String(paymentDataForm.sienge_paymentCategoriesId || '').trim(),
      sienge_documentIdentificationId: !String(paymentDataForm.sienge_documentIdentificationId || '').trim(),
      sienge_debtorId: !String(paymentDataForm.sienge_debtorId || '').trim(),
      sienge_costCenterId: !String(paymentDataForm.sienge_costCenterId || '').trim(),
      sienge_baseDate: !String(paymentDataForm.sienge_baseDate || '').trim(),
      sienge_dueDate: !String(paymentDataForm.sienge_dueDate || '').trim(),
      sienge_notes: !String(paymentDataForm.sienge_notes || '').trim(),
    };
    if (Object.values(nextErrors).some(Boolean)) {
      setPaymentDataErrors(nextErrors);
      return;
    }
    setPaymentDataErrors({
      creditorName: false,
      document: false,
      sienge_totalInvoiceAmount: false,
      sienge_paymentCategoriesId: false,
      sienge_documentIdentificationId: false,
      sienge_debtorId: false,
      sienge_costCenterId: false,
      sienge_baseDate: false,
      sienge_dueDate: false,
      sienge_notes: false,
    });
    try {
      setUpdatingPaymentData(true);
      const result = await app.updateLotPaymentData(effectiveImportId, {
        creditor_name: paymentDataForm.creditorName,
        document: normalizedDoc,
        sienge_totalInvoiceAmount: Number(paymentDataForm.sienge_totalInvoiceAmount),
        sienge_paymentCategoriesId: paymentDataForm.sienge_paymentCategoriesId,
        sienge_documentIdentificationId: paymentDataForm.sienge_documentIdentificationId,
        sienge_debtorId: Number(paymentDataForm.sienge_debtorId),
        sienge_costCenterId: Number(paymentDataForm.sienge_costCenterId),
        sienge_documentNumber: paymentDataForm.documentNumber,
        sienge_baseDate: paymentDataForm.sienge_baseDate,
        sienge_dueDate: paymentDataForm.sienge_dueDate,
        sienge_notes: paymentDataForm.sienge_notes,
      });
      app.toast('success', 'Dados do Pagamento', result?.message || 'Dados atualizados com sucesso.');
      await app.syncFromApi(undefined, 'batches');
      await loadImportDetails(effectiveImportId, { current: false });
    } catch (error) {
      app.toast('error', 'Dados do Pagamento', error?.message || 'Falha ao atualizar dados.');
    } finally {
      setUpdatingPaymentData(false);
    }
  }, [app, effectiveImportId, loadImportDetails, paymentDataForm, requiredDocLength, canMutatePayment]);

  if (!app.workspaceReady || !detailsReady) {
    return <SpWorkspaceLoading active="payments" title="A carregar detalhes do pagamento…" />;
  }

  return (
    <PShell active="payments">
      <div className="sp-batch-breadcrumb">
        <button type="button" className="sp-btn sp-btn-ghost sp-btn-back-compact" onClick={() => app.navigate('payments')}>
          <span className="sp-icon--flip"><Icon name="arrow" size={13} /></span> Pagamentos
        </button>
        <span className="sp-batch-breadcrumb__sep">/</span>
        <span className="sp-mono sp-batch-breadcrumb__id">{batch.id}</span>
        <div style={{ flex: 1 }} />
        <button type="button" className="sp-btn sp-btn-ghost sp-btn-back-compact" onClick={() => window.print()} title="Imprimir">
          <Icon name="printer" size={16} />
        </button>
      </div>
      <div className="sp-screen-head">
        <div>
          <h1 className="sp-screen-head__title">Detalhes do(s) Pagamento(s)</h1>
          <div className="sp-mono sp-screen-head__meta">{batch.file} · criado em {batch.createdAtLabel} por {creatorName}</div>
        </div>
        <div className="sp-batch-actions">
          <StatusChip kind={s.kind}>{s.label}</StatusChip>
          {canDelete && (
            <button
              type="button"
              className="sp-btn sp-btn-outline sp-btn-delete-confirm"
              disabled={approvalBlocked || syncBlocked}
              onClick={approvalBlocked ? () => setApprovalBlockedModal(true) : openDelete}
            >
              <Icon name="trash" size={13} stroke={2} /> Excluir
            </button>
          )}
        </div>
      </div>

      <div className="sp-batch-kpi-grid">
        {[
          ['Valor total', batch.total],
          ['Data de Vencimento', batch.dueDateRaw
            ? (() => { const [y, m, d] = batch.dueDateRaw.split('-'); return `${d}/${m}/${y}`; })()
            : '—'],
          ['Lote', batch.id],
          ['Transmitido em', batch.transmittedAt
            ? (() => { const d = new Date(batch.transmittedAt + 'Z'); const hh = String(d.getHours()).padStart(2, '0'); const mm = String(d.getMinutes()).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); const MM = String(d.getMonth() + 1).padStart(2, '0'); return `${hh}:${mm} - ${dd}/${MM}/${d.getFullYear()}`; })()
            : '—'],
        ].map(([k, v], i) => (
          <div key={i} className="sp-card sp-batch-kpi">
            <div className="sp-caps">{k}</div>
            <div className="sp-num sp-batch-kpi__val">{v}</div>
          </div>
        ))}
      </div>

      <div className="sp-batch-split">
        <div className="sp-batch-left-stack">
          <div className="sp-card sp-card-flush">
            <div className="sp-batch-table-head">Pagamentos transmitidos</div>
            <table className="sp-table">
              <thead><tr><th>Credor</th><th>Doc. Sienge</th><th>Status</th><th>BillId Sienge</th><th className="sp-th-num">Valor</th></tr></thead>
              <tbody>
                {siengeLoading ? (
                  <tr><td colSpan="5" className="sp-batch-empty-row">A carregar…</td></tr>
                ) : siengeRows.length === 0 ? (
                  <tr><td colSpan="5" className="sp-batch-empty-row">Nenhum pagamento transmitido.</td></tr>
                ) : (
                  siengeRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.creditorName}</td>
                      <td className={row.docKind === 'mono' ? 'sp-mono' : undefined}>
                        {row.docTag ? <span className={`sp-chip sp-chip-${row.docKind || 'amber'}`}>{row.docTag}</span> : row.doc}
                      </td>
                      <td>
                        {row.statusTag
                          ? <span className={`sp-chip sp-chip-${row.statusKind || 'amber'}`}>{row.statusTag}</span>
                          : <span className="sp-chip sp-chip-green">{row.statusLabel}</span>}
                      </td>
                      <td className="sp-mono">
                        {row.billId !== '—' ? (
                          <button type="button" className="sp-btn sp-btn-ghost sp-btn-compact sp-mono" style={{ color: 'var(--ft-green)', textDecoration: 'underline', padding: 0, fontSize: 'inherit' }} onClick={() => {
                            setBillModalOpen(true);
                            setBillModalLoading(true);
                            setBillModalData(null);
                            setBillModalError(null);
                            const url = `${CONFIG_BACKEND.baseUrl}/api/companies/${batch.companyId}/sienge-bills/${row.billId}`;
                            fetch(url, { headers: { Accept: 'application/json', Authorization: `Bearer ${app.authToken}` } })
                              .then((r) => r.json())
                              .then((data) => { setBillModalData(data); setBillModalLoading(false); })
                              .catch((e) => { setBillModalError(e.message || 'Falha ao carregar'); setBillModalLoading(false); });
                          }}>
                            {row.billId}
                          </button>
                        ) : '—'}
                      </td>
                      <td className="sp-num sp-td-num">{row.amount}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="sp-card sp-batch-payment-data">
            <div className="sp-batch-payment-data__title">Dados do Credor</div>
            {batch.status !== 'transmitted' && (
                <div className="sp-field-block">
                  <label className="sp-caps sp-label">Busca dinâmica por nome</label>
                  <input
                    className="sp-input"
                    value={creditorSearch}
                    onChange={(e) => {
                      setCreditorSearch(e.target.value);
                      setSelectedCreditorId('');
                    }}
                    placeholder="Digite o nome ou parte do nome do credor"
                  />
                  {creditorSearch.trim().length > 1 && (
                    <div className="sp-newpayment-template-card" style={{ marginTop: 8 }}>
                      {(() => {
                        const term = creditorSearch.trim();
                        const filtered = creditorOptions
                          .filter((c) => String(c?.name || '').toLowerCase().includes(term.toLowerCase()))
                          .slice(0, 8);
                        if (!filtered.length) {
                          return <div className="sp-sidebar-card__muted">Não foram encontrados resultados para a busca: "{term}"</div>;
                        }
                        return filtered.map((c) => {
                          const selected = String(selectedCreditorId || '') === String(c.id || '');
                          const doc = selectedTemplate?.strat === 'BY_CNPJ' ? normalizeDoc(c?.cnpj) : normalizeDoc(c?.cpf);
                          return (
                            <button
                              key={String(c.id)}
                              type="button"
                              className={`sp-btn sp-btn-outline sp-btn-tiny sp-newpayment-creditor-btn${selected ? ' is-active' : ''}${doc ? ' has-doc' : ' missing-doc'}`}
                              style={{ marginRight: 6, marginBottom: 6 }}
                              onClick={async () => {
                                setSelectedCreditorId(String(c.id || ''));
                                setPaymentCreditorSiengeId(String(c?.sienge_creditorId ?? ''));
                                setPaymentDataForm((s0) => ({
                                  ...s0,
                                  creditorName: String(c?.name || ''),
                                  document: doc,
                                }));
                                try {
                                  const prefs = await app.fetchCreditorPreferences(batch.companyId, c.id);
                                  if (prefs?.sienge_debtorId || prefs?.sienge_costCenterId) {
                                    setPaymentDataForm((s0) => ({
                                      ...s0,
                                      ...(prefs.sienge_debtorId     ? { sienge_debtorId:    String(prefs.sienge_debtorId) }     : {}),
                                      ...(prefs.sienge_costCenterId ? { sienge_costCenterId: String(prefs.sienge_costCenterId) } : {}),
                                    }));
                                  }
                                } catch { /* sem preferências — ignora */ }
                              }}
                            >
                              {c.name}
                            </button>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
            )}
                <div className="sp-batch-payment-data__grid">
                  <div className="sp-field-block">
                    <label className="sp-caps sp-label">Nome Completo</label>
                    <input
                      className={`sp-input${paymentDataErrors.creditorName ? ' is-invalid' : ''}`}
                      value={paymentDataForm.creditorName}
                      onChange={(e) => {
                        setPaymentDataForm((s0) => ({ ...s0, creditorName: e.target.value }));
                        setPaymentDataErrors((s0) => ({ ...s0, creditorName: false }));
                      }}
                      disabled={!canMutatePayment}
                    />
                  </div>
                  <div className={`sp-field-block${paymentDataErrors.document ? ' sp-field-block--invalid' : ''}`}>
                    <label className="sp-caps sp-label">{documentFieldLabel}</label>
                    <input
                      className={`sp-input sp-mono${paymentDataErrors.document ? ' is-invalid' : ''}`}
                      value={formatDocument(paymentDataForm.document)}
                      onChange={(e) => {
                        setPaymentDataForm((s0) => ({ ...s0, document: normalizeDoc(e.target.value) }));
                        setPaymentDataErrors((s0) => ({ ...s0, document: false }));
                      }}
                      disabled={!canMutatePayment}
                    />
                  </div>
            </div>
          </div>
          <div className="sp-card sp-batch-payment-data">
            <div className="sp-batch-payment-data__title">Dados de Pagamento</div>
            <div className="sp-newpayment-grid" style={{ padding: 0 }}>
                  <div className={`sp-field-block${paymentDataErrors.sienge_totalInvoiceAmount ? ' sp-field-block--invalid' : ''}`}>
                    <label className="sp-caps sp-label">
                      Valor do Pagamento <span className="sp-sienge-field-id">[TotalInvoiceAmount]</span>
                    </label>
                    <input
                      className={`sp-input sp-mono${paymentDataErrors.sienge_totalInvoiceAmount ? ' is-invalid' : ''}`}
                      value={paymentDataForm.sienge_totalInvoiceAmount}
                      onChange={(e) => {
                        setPaymentDataForm((s0) => ({ ...s0, sienge_totalInvoiceAmount: e.target.value }));
                        setPaymentDataErrors((s0) => ({ ...s0, sienge_totalInvoiceAmount: false }));
                      }}
                      disabled={!canMutatePayment}
                    />
                  </div>
                  <div className="sp-field-block">
                    <label className="sp-caps sp-label">Número do Documento</label>
                    <input
                      className="sp-input sp-mono"
                      value={paymentDataForm.documentNumber}
                      disabled
                      readOnly
                    />
                  </div>

                  <fieldset className="sp-fieldset sp-fieldset--full">
                    <legend className="sp-legend">Pagador</legend>
                    <div className={`sp-field-block${paymentDataErrors.sienge_debtorId ? ' sp-field-block--invalid' : ''}`}>
                      <label className="sp-caps sp-label">
                        Companhia <span className="sp-sienge-field-id">[DebtorId]</span>
                      </label>
                      <select
                        className={`sp-input${paymentDataErrors.sienge_debtorId ? ' is-invalid' : ''}`}
                        value={paymentDataForm.sienge_debtorId}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPaymentDataForm((s0) => ({ ...s0, sienge_debtorId: v, sienge_costCenterId: '' }));
                          setPaymentDataErrors((s0) => ({ ...s0, sienge_debtorId: false, sienge_costCenterId: false }));
                        }}
                        disabled={!canMutatePayment}
                      >
                        <option value="">Selecione</option>
                        {siengeCompanyOptions.map((opt) => (
                          <option key={String(opt?.sienge_debtorId || opt?.id)} value={String(opt?.sienge_debtorId || '')}>
                            {String(opt?.name || '')}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={`sp-field-block${paymentDataErrors.sienge_costCenterId ? ' sp-field-block--invalid' : ''}`}>
                      <label className="sp-caps sp-label">
                        Centro de Custo <span className="sp-sienge-field-id">[CostCenterId]</span>
                      </label>
                      <select
                        className={`sp-input${paymentDataErrors.sienge_costCenterId ? ' is-invalid' : ''}`}
                        value={paymentDataForm.sienge_costCenterId}
                        onChange={(e) => {
                          setPaymentDataForm((s0) => ({ ...s0, sienge_costCenterId: e.target.value }));
                          setPaymentDataErrors((s0) => ({ ...s0, sienge_costCenterId: false }));
                        }}
                        disabled={!canMutatePayment || !String(paymentDataForm.sienge_debtorId || '').trim()}
                      >
                        <option value="">Selecione</option>
                        {siengeCostCenterOptions.map((opt) => (
                          <option key={String(opt?.sienge_costCenterId || opt?.id)} value={String(opt?.sienge_costCenterId || '')}>
                            {String(opt?.name || '')}
                          </option>
                        ))}
                      </select>
                    </div>
                  </fieldset>

                  <fieldset className="sp-fieldset sp-fieldset--full">
                    <legend className="sp-legend">Datas</legend>
                    <div className={`sp-field-block${paymentDataErrors.sienge_baseDate ? ' sp-field-block--invalid' : ''}`}>
                      <label className="sp-caps sp-label">
                        Data Base <span className="sp-sienge-field-id">[BaseDate]</span>
                      </label>
                      <input
                        type="date"
                        className={`sp-input sp-mono${paymentDataErrors.sienge_baseDate ? ' is-invalid' : ''}`}
                        value={paymentDataForm.sienge_baseDate}
                        onChange={(e) => {
                          setPaymentDataForm((s0) => ({ ...s0, sienge_baseDate: e.target.value }));
                          setPaymentDataErrors((s0) => ({ ...s0, sienge_baseDate: false }));
                        }}
                        disabled={!canMutatePayment}
                      />
                    </div>
                    <div className={`sp-field-block${paymentDataErrors.sienge_dueDate ? ' sp-field-block--invalid' : ''}`}>
                      <label className="sp-caps sp-label">
                        Data de vencimento <span className="sp-sienge-field-id">[DueDate]</span>
                      </label>
                      <input
                        type="date"
                        className={`sp-input sp-mono${paymentDataErrors.sienge_dueDate ? ' is-invalid' : ''}`}
                        value={paymentDataForm.sienge_dueDate}
                        onChange={(e) => {
                          setPaymentDataForm((s0) => ({ ...s0, sienge_dueDate: e.target.value }));
                          setPaymentDataErrors((s0) => ({ ...s0, sienge_dueDate: false }));
                        }}
                        disabled={!canMutatePayment}
                      />
                    </div>
                  </fieldset>

                  <fieldset className="sp-fieldset sp-fieldset--full">
                    <legend className="sp-legend">Tipo</legend>
                    <div className={`sp-field-block${paymentDataErrors.sienge_paymentCategoriesId ? ' sp-field-block--invalid' : ''}`}>
                      <label className="sp-caps sp-label">
                        Categoria <span className="sp-sienge-field-id">[PaymentCategoriesId]</span>
                      </label>
                      <select
                        className={`sp-input${paymentDataErrors.sienge_paymentCategoriesId ? ' is-invalid' : ''}`}
                        value={paymentDataForm.sienge_paymentCategoriesId}
                        onChange={(e) => {
                          setPaymentDataForm((s0) => ({ ...s0, sienge_paymentCategoriesId: e.target.value }));
                          setPaymentDataErrors((s0) => ({ ...s0, sienge_paymentCategoriesId: false }));
                        }}
                        disabled={!canMutatePayment}
                      >
                        <option value="">Selecione</option>
                        {paymentCategoryOptions.map((opt) => (
                          <option key={String(opt?.value || '')} value={String(opt?.value || '')}>
                            {`${String(opt?.name || '')}${String(opt?.value || '').trim() ? ` (${String(opt?.value || '')})` : ''}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={`sp-field-block${paymentDataErrors.sienge_documentIdentificationId ? ' sp-field-block--invalid' : ''}`}>
                      <label className="sp-caps sp-label">
                        ID do Documento <span className="sp-sienge-field-id">[DocumentIdentificationId]</span>
                      </label>
                      <select
                        className={`sp-input${paymentDataErrors.sienge_documentIdentificationId ? ' is-invalid' : ''}`}
                        value={paymentDataForm.sienge_documentIdentificationId}
                        onChange={(e) => {
                          setPaymentDataForm((s0) => ({ ...s0, sienge_documentIdentificationId: e.target.value }));
                          setPaymentDataErrors((s0) => ({ ...s0, sienge_documentIdentificationId: false }));
                        }}
                        disabled={!canMutatePayment}
                      >
                        <option value="">Selecione</option>
                        {documentIdentificationOptions.map((opt) => (
                          <option key={String(opt?.value || '')} value={String(opt?.value || '')}>
                            {`${String(opt?.name || '')}${String(opt?.value || '').trim() ? ` (${String(opt?.value || '')})` : ''}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </fieldset>

                  <fieldset className="sp-fieldset sp-fieldset--single sp-fieldset--full">
                    <legend className="sp-legend">Anotações</legend>
                    <div className={`sp-field-block${paymentDataErrors.sienge_notes ? ' sp-field-block--invalid' : ''}`}>
                      <textarea
                        className={`sp-input${paymentDataErrors.sienge_notes ? ' is-invalid' : ''}`}
                        value={paymentDataForm.sienge_notes}
                        onChange={(e) => {
                          const val = e.target.value.slice(0, 450);
                          setPaymentDataForm((s0) => ({ ...s0, sienge_notes: val }));
                          setPaymentDataErrors((s0) => ({ ...s0, sienge_notes: false }));
                        }}
                        maxLength={450}
                        rows={3}
                        disabled={!canMutatePayment}
                      />
                      <div className="sp-char-counter">
                        {String(paymentDataForm.sienge_notes || '').length}/450
                      </div>
                    </div>
                  </fieldset>
            </div>
            {(isAdmin || !isRegistrar || isOwner) && batch.status !== 'transmitted' && (
              <div className="sp-batch-payment-data__actions">
                <button
                  type="button"
                  className="sp-btn sp-btn-accent"
                  disabled={approvalBlocked || syncBlocked || updatingPaymentData || siengeLoading}
                  onClick={approvalBlocked ? () => setApprovalBlockedModal(true) : updatePaymentData}
                >
                  {updatingPaymentData ? 'Atualizando dados…' : 'Atualizar dados'}
                </button>
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {batch.status === 'rejected' && rejectionInfo && (
            <div className="sp-card sp-card-flush">
              <div className="sp-batch-table-head" style={{ color: 'var(--ft-purple)' }}>Detalhes da Rejeição</div>
              <table className="sp-table">
                <thead>
                  <tr><th>Rejeitado por</th><th>Data Ult. Rejeição</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{rejectionInfo.reviewer}</td>
                    <td className="sp-mono sp-td-date">
                      {rejectionInfo.reviewedAt
                        ? new Date(String(rejectionInfo.reviewedAt).replace(' ', 'T') + 'Z').toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
              {rejectionInfo.notes && (() => {
                let entries;
                try { entries = JSON.parse(rejectionInfo.notes); } catch { entries = null; }
                const lines = Array.isArray(entries) ? entries : [rejectionInfo.notes];
                return (
                  <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
                    <div className="sp-caps sp-label" style={{ marginBottom: 6 }}>
                      {lines.length > 1 ? `Motivos da rejeição (${lines.length})` : 'Motivo da rejeição'}
                    </div>
                    {lines.map((line, i) => (
                      <div key={i} style={{ fontSize: 13, paddingTop: i > 0 ? 8 : 0, marginTop: i > 0 ? 8 : 0, borderTop: i > 0 ? '1px dashed var(--border)' : 'none' }}>
                        {lines.length > 1 && <span className="sp-caps" style={{ fontSize: 10, opacity: 0.6, marginRight: 6 }}>#{i + 1}</span>}
                        {line}
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div style={{ padding: '12px 14px', display: 'flex', justifyContent: 'center' }}>
                <button
                  type="button"
                  className="sp-btn sp-batch-resubmit-btn"
                  onClick={async () => {
                    if (!batch.approvalId) { app.toast('error', 'Reenvio', 'Aprovação não encontrada.'); return; }
                    try {
                      await app.resubmitApproval(batch.approvalId);
                      app.toast('success', 'Reenvio', 'Pagamento reenviado para aprovação.');
                      app.navigate('payments');
                    } catch (e) {
                      app.toast('error', 'Reenvio', e?.message || 'Falha ao reenviar.');
                    }
                  }}
                >
                  Reenviar para Aprovação
                </button>
              </div>
            </div>
          )}
          <div className="sp-card sp-batch-timeline">
          {batch.status === 'pending' && effectiveImportId && (
            <div className="sp-batch-timeline-actions">
              <button type="button" className="sp-btn sp-batch-timeline-pendency-btn" onClick={verifyPendencies} disabled={!canMutatePayment || verifyingPending}>
                <span className="sp-batch-timeline-pendency-btn__icon" aria-hidden>
                  <Icon name="alert" size={13} />
                </span>
                {verifyingPending ? 'Verificando pendências…' : 'Verificar Pendência(s)'}
              </button>
            </div>
          )}
          <div className="sp-batch-timeline__title">Timeline</div>
          {batch.id === '-' ? (
            <div className="sp-batch-timeline-empty">Sem eventos de timeline.</div>
          ) : (
            <>
              {batch.transmittedAt && (
                <div className="sp-batch-timeline-row">
                  <div className="sp-batch-timeline-dot" style={{ background: 'var(--ft-green)', borderColor: 'var(--ft-green)' }} aria-hidden />
                  <div className="sp-batch-timeline-body">
                    <div className="sp-batch-timeline-act">
                      <span className="sp-batch-timeline-lead">Lote transmitido em</span>
                      <span className="sp-batch-timeline-datetime">
                        {(() => { const d = new Date(batch.transmittedAt + 'Z'); const hh = String(d.getHours()).padStart(2, '0'); const mm = String(d.getMinutes()).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); const MM = String(d.getMonth() + 1).padStart(2, '0'); return `${hh}:${mm} - ${dd}/${MM}/${d.getFullYear()}`; })()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {orderedIdentificationChecks.map((check, idx) => {
                const label = check?.source === 'manual_recheck'
                  ? 'Verificação de pendência'
                  : check?.source === 'manual_update'
                    ? 'Atualização de dados'
                    : 'Validação inicial';
                const when = check?.timestamp
                  ? new Date(check.timestamp).toLocaleString('pt-BR')
                  : '—';
                const localLabel = check?.identified_local ? 'Identificado' : 'Não Identificado';
                const siengeLabel = check?.identified_sienge ? 'Identificado' : 'Não Identificado';
                return (
                  <React.Fragment key={`${label}-${when}-${idx}`}>
                    <div className="sp-batch-timeline-row sp-batch-timeline-row--event-title">
                      <div className="sp-batch-timeline-dot" aria-hidden />
                      <div className="sp-batch-timeline-body">
                        <div className="sp-batch-timeline-event-title">{label}:</div>
                      </div>
                    </div>
                    <div className="sp-batch-timeline-row">
                      <div className="sp-batch-timeline-dot" aria-hidden />
                      <div className="sp-batch-timeline-body">
                        <div className="sp-batch-timeline-act">
                          <span className="sp-batch-timeline-lead">Local</span>
                          <span className={`sp-chip${check?.identified_local ? ' sp-chip-green' : ' sp-chip-red'}`}>{localLabel}</span>
                          <span className="sp-batch-timeline-lead"> em </span>
                          <span className="sp-batch-timeline-datetime">{when}</span>
                        </div>
                      </div>
                    </div>
                    <div className="sp-batch-timeline-row">
                      <div className="sp-batch-timeline-dot" aria-hidden />
                      <div className="sp-batch-timeline-body">
                        <div className="sp-batch-timeline-act">
                          <span className="sp-batch-timeline-lead">Sienge</span>
                          <span className={`sp-chip${check?.identified_sienge ? ' sp-chip-green' : ' sp-chip-red'}`}>{siengeLabel}</span>
                          <span className="sp-batch-timeline-lead"> em </span>
                          <span className="sp-batch-timeline-datetime">{when}</span>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div className="sp-batch-timeline-row">
                <div className="sp-batch-timeline-dot" aria-hidden />
                <div className="sp-batch-timeline-body">
                  <div className="sp-batch-timeline-status-line">
                    <span className="sp-batch-timeline-lead">Status Local:</span>
                    <span className={`sp-chip${localOk ? ' sp-chip-green' : ' sp-chip-red'}`}>
                      {localOk ? 'Identificado' : 'Não Identificado'}
                    </span>
                    <span className="sp-batch-timeline-lead"> em </span>
                    <span className="sp-batch-timeline-datetime sp-batch-timeline-datetime--inline">{createdLabel}</span>
                  </div>
                </div>
              </div>
              <div className="sp-batch-timeline-row">
                <div className="sp-batch-timeline-dot" aria-hidden />
                <div className="sp-batch-timeline-body">
                  <div className="sp-batch-timeline-status-line">
                    <span className="sp-batch-timeline-lead">Status Sienge:</span>
                    <span className={`sp-chip${siengeOk ? ' sp-chip-green' : ' sp-chip-red'}`}>
                      {siengeOk ? 'Identificado' : 'Não Identificado'}
                    </span>
                    <span className="sp-batch-timeline-lead"> em </span>
                    <span className="sp-batch-timeline-datetime sp-batch-timeline-datetime--inline">{createdLabel}</span>
                  </div>
                </div>
              </div>
              <div className="sp-batch-timeline-row">
                <div className="sp-batch-timeline-dot" aria-hidden />
                <div className="sp-batch-timeline-body">
                  <div className="sp-batch-timeline-act">
                    <span className="sp-batch-timeline-lead">Criado em</span>
                    <span className="sp-batch-timeline-datetime">{createdLabel}</span>
                    <span className="sp-batch-timeline-lead">pelo usuário</span>
                    <span className="sp-batch-timeline-user">{creatorName}</span>
                  </div>
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      </div>
      <DeletePaymentConfirmDialog
        open={deleteOpen}
        batchId={batch.id}
        onCancel={closeDelete}
        onConfirm={confirmDelete}
        busy={deleteBusy}
      />
      <Modal open={approvalBlockedModal} onClose={() => setApprovalBlockedModal(false)} width={460}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Pagamento bloqueado para aprovação</div>
            <div className="sp-modal__head-desc">Ele só poderá ser editado se for rejeitado pelos aprovadores.</div>
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-modal__close" onClick={() => setApprovalBlockedModal(false)}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--stack-lg">
          <div className="sp-modal-actions">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => setApprovalBlockedModal(false)}>
              Entendi
            </button>
          </div>
        </div>
      </Modal>
      <Modal open={billModalOpen} onClose={() => { setBillModalOpen(false); setBillModalData(null); setBillModalError(null); }} width={720}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Detalhes do Bill Sienge</div>
            <div className="sp-modal__head-desc">Resposta da API /v1/bills</div>
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-modal__close" onClick={() => { setBillModalOpen(false); setBillModalData(null); setBillModalError(null); }}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body">
          {billModalLoading ? (
            <div className="sp-batch-empty-row">A carregar…</div>
          ) : billModalError ? (
            <div className="sp-creditors-banner sp-creditors-banner--err">{billModalError}</div>
          ) : billModalData ? (
            <pre style={{ fontSize: 11, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, maxHeight: '70vh', overflow: 'auto' }}>
              {JSON.stringify(billModalData, null, 2)}
            </pre>
          ) : null}
        </div>
      </Modal>
    </PShell>
  );
}
